import {defaultTo, pipe, values, length, uniqBy, join, reduce, cond, prop, T} from 'ramda';

import {ProductType} from 'constants/flow/flowTypesProduct';
import {SimpleProductType} from '@typescript/productTypes';
import {ProductType as ProductV2Type} from '@page/wishlist/types';
import {QuoteItemType} from 'constants/flow/flowTypesCart';
import {PRODUCT_TYPE} from 'constants/analytics/productType';
import {PRODUCT_TYPES} from 'constants/product';
import {CATEGORY_LIST} from 'constants/analytics/storages';
import {CheckoutQuoteProductV2Type} from 'reducers/checkout/orderReview/orderReviewType';

import {localStore} from 'libs/storage/storage';
import {BRONTO_BADGES} from '../../constants/bronto/bronto';

export const defaultOptions = {
    index: 0,
    category: '',
    list: '/',
    parentName: '',
};

/**
 * Return empty string if get falsely value
 */
export const emptyByDefault = defaultTo('');

/**
 * Return object size
 */
export const size = pipe(values, length);

/**
 * Get number unique products
 * @param {ProductType} product - Product data
 * @param {string} field - Filed name
 * @returns {number} number unique products
 */
export const getNumberUniqueProducts = ({children}: ProductType, field: string): number =>
    uniqBy(product => product[field], children as SimpleProductType[]).length;

/**
 * Return contacts color
 * @param {QuoteItemType} product - quote item
 * @return {string} color
 */
export const getContactsColor = (product: QuoteItemType): string => {
    const {prescription} = product;
    if (prescription && prescription.od && prescription.os) {
        return join(
            ', ',
            reduce(
                (acc: Array<string>, color?: string) => [...acc, color || 'None'],
                [],
                [prescription.od.color, prescription.os.color],
            ),
        );
    }
    return '';
};

/**
 * Return product type
 * @param {string} type - type
 * @param {boolean} isPrescription - prescription
 * @return {string} color
 */
export const getProductType = (type: string, isPrescription = true): string => {
    const ProductTypes = {
        readers: PRODUCT_TYPE.READERS,
        sunglasses: isPrescription ? PRODUCT_TYPE.SUNGLASSES : PRODUCT_TYPE.SUNGLASSES_PLANO,
        contact_lenses: PRODUCT_TYPE.CONTACTS,
        eyeglasses: PRODUCT_TYPE.EYEGLASSES,
        accessories: PRODUCT_TYPE.ACCESSORIES,
    };

    return ProductTypes[type] || emptyByDefault(type);
};

/**
 * Get product category
 * @param {QuoteItemType} product - Product data.
 * @returns {string} Product category
 */
export const getCategory = (product: QuoteItemType): string => {
    const {type, product_id: id} = product;
    if (type === PRODUCT_TYPES.CONTACT_LENSES) return 'Contact Lenses';
    const categoryList = localStore.get(CATEGORY_LIST) || [];
    const item = categoryList.find(category => Number(category.id) === Number(id));
    if (item) return item.category || 'Non category page';
    return 'Non category page';
};

/**
 * Get product category from new checkout endpoint
 * @param {CheckoutQuoteProductV2Type} product - Product data.
 * @returns {string} Product category
 */
export const getCategoryV2 = (product: CheckoutQuoteProductV2Type): string => {
    const {type, id} = product;
    if (type === PRODUCT_TYPES.CONTACT_LENSES) return 'Contact Lenses';
    const categoryList = localStore.get(CATEGORY_LIST) || [];
    const item = categoryList.find(category => Number(category.id) === Number(id));
    if (item) return item.category || 'Non category page';
    return 'Non category page';
};

/**
 * Return prescription type.
 * @param {QuoteItemType} product - Product data.
 * @returns {string} prescription type.
 */
export const getTypePrescription = ({prescription, type}: QuoteItemType): string => {
    if (!prescription) return '';
    const {type: prescriptionType, name} = prescription;
    const PRESCRIPTION_TYPES: {[key: string]: string} = {
        online: type === PRODUCT_TYPES.CONTACT_LENSES || !name ? 'Filled in' : 'Saved prescription',
        email: 'Send later',
        upload: 'Uploaded',
    };

    return PRESCRIPTION_TYPES[prescriptionType] || '';
};

/**
 * Check product type for glasses
 *
 * @param {string} type product.
 * @returns {boolean} Is glasses result.
 */
export const isGlasses = (type: string) => {
    if (!type) return '';

    const glasses = ['sunglasses', 'eyeglasses', 'upgrade', 'readers'];

    return glasses.includes(type);
};

/**
 * Get product badge
 * @param {SimpleProductType | QuoteItemType | CheckoutQuoteItemType} product - product
 * @return {string} - badge
 */
export const getBadge = (product: QuoteItemType | SimpleProductType | ProductV2Type): string => {
    if (!product) return '';

    if ('product_badge' in product && product.product_badge.length) {
        const badgeItem = product.product_badge[0];
        return BRONTO_BADGES[badgeItem];
    }

    if ('badges' in product && product.badges.length) {
        const badgeItem = (product as SimpleProductType).badges[0];
        return BRONTO_BADGES[badgeItem.id];
    }

    return '';
};

/**
 * Format the url by adding the ".html" extension at the end if it is not there
 * @param {string} url - url path
 * @return {string} - formatted path
 */
export const formatStringFromUrl = (url: string) => (url.split('.')[1] ? url : `${url}.html`);

/**
 * Get product frame_url or url
 * @param {SimpleProductType | QuoteItemType | CheckoutQuoteProductV2Type} product - product
 * @return {string} - badge
 */
export const getFrameUrl = cond([
    [prop('url'), (product: SimpleProductType) => `${formatStringFromUrl(product.url) || ''}`],
    [
        prop('urlPath'),
        (product: CheckoutQuoteProductV2Type) => `${formatStringFromUrl(product.urlPath) || ''}`,
    ],
    [
        prop('frame_url_path'),
        (product: QuoteItemType) => `${formatStringFromUrl(product.frame_url_path) || ''}`,
    ],
    [T, () => ''],
]);
